const THIRTY_MINUTES_IN_MS = 30 * 60 * 1000;

export const minStartTime = () => {
  const date = new Date();
  date.setHours(0, 0, 0); // 12:00 AM

  return date;
};

export const minEndTime = (startTime) => {
  let startingPoint;

  if (!startTime) {
    startingPoint = minStartTime();
  } else {
    startingPoint = new Date(startTime);
  }

  // whatever startingPoint is, we want to return 30 minutes after it.
  return new Date(startingPoint.getTime() + THIRTY_MINUTES_IN_MS);
};

export const maxEndTime = () => {
  const date = new Date();
  date.setHours(23, 30, 0); // 11:30 PM

  return date;
};

export const maxStartTime = (endTime) => {
  let startingPoint;

  if (!endTime) {
    startingPoint = maxEndTime();
  } else {
    startingPoint = new Date(endTime);
  }

  // whatever startingPoint is, we want to return 30 minutes before it.
  return new Date(startingPoint.getTime() - THIRTY_MINUTES_IN_MS);
};

export const completedAvailabilities = (availabilities) => (
  availabilities.filter((a) => a.date && a.start_time && a.end_time)
);

const overlaps = (availability1, availability2) => {
  if (availability1.date.toDateString() !== availability2.date.toDateString()) {
    return false;
  }

  const a1Start = availability1.start_time.getTime();
  const a1End = availability1.end_time.getTime();
  const a2Start = availability2.start_time.getTime();
  const a2End = availability2.end_time.getTime();

  return Math.max(a1Start, a2Start) <= Math.min(a1End, a2End);
};

export const overlappingAvailabilities = (availabilities) => {
  const completeAvailabilities = completedAvailabilities(availabilities);
  const overlappingIndices = [];

  // naive O(n^2) implementation - can be done in O(n * logn) but I think for this use-case
  // the performance gain would be minimal.
  // - S.C.
  for (let i = 0; i < completeAvailabilities.length - 1; i += 1) {
    for (let j = i + 1; j < completeAvailabilities.length; j += 1) {
      if (overlaps(completeAvailabilities[i], completeAvailabilities[j])) {
        overlappingIndices.push(i);
        overlappingIndices.push(j);
      }
    }
  }

  return overlappingIndices;
};

export const startTimeInPastValidator = (availabilities) => {
  const currentTime = new Date();
  for (let i = 0; i < availabilities.length; i += 1) {
    const avail = availabilities[i];
    if (avail.date <= currentTime && avail.start_time && avail.start_time < currentTime) {
      return true;
    }
  }
  return false;
};

export const formatToRubyStyle = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => formatToRubyStyle(item));
  } if (typeof obj === 'object' && obj !== null) {
    const result = Object.keys(obj).reduce((acc, key) => {
      // Convert camelCase to snake_case
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      const value = obj[key];

      if (snakeKey === 'start_time' && typeof value === 'string' && moment(value, ['h:mm A'], true).isValid()) {
        // Format start_time to HH:mm
        acc[snakeKey] = moment(value, 'h:mm A').format('HH:mm');
      } else if (snakeKey === 'date') {
        acc[snakeKey] = value; // Keep date as-is
      }

      return acc;
    }, {});

    // Add end_time if start_time exists
    if (result.start_time && !result.end_time) {
      const startTime = moment(result.start_time, 'HH:mm');
      result.end_time = startTime.add(30, 'minutes').format('HH:mm');
    }

    return result;
  }

  return obj; // Return non-object/non-array values as-is
};

export const formatToJavaScriptStyle = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => formatToJavaScriptStyle(item));
  } if (typeof obj === 'object' && obj !== null) {
    return Object.keys(obj).reduce((acc, key) => {
      // Convert snake_case to camelCase
      const camelKey = key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
      const value = obj[key];

      // Only add date and startTime keys to the result object
      if (camelKey === 'date') {
        acc[camelKey] = value;
      } else if (camelKey === 'startTime' && typeof value === 'string' && moment(value, 'HH:mm', true).isValid()) {
        acc[camelKey] = moment(value, 'HH:mm').format('hh:mm A');
      }

      return acc;
    }, {});
  }

  return obj; // Return non-object/non-array values as-is
};
